import { Query } from "@feathersjs/feathers/lib";
import { addWeeks, endOfMonth, isAfter,isToday, isBefore, startOfMonth } from "date-fns";
import { makeObservable, reaction, runInAction } from "mobx";
import { RootStore, ItemStore } from ".";
import { Deliverable } from "../models";
import { DataType, IDeliverable } from "../types";
import { DataContainer } from "./dataContainer";

const fiveDaysAheadDate = new Date();
fiveDaysAheadDate.setDate(fiveDaysAheadDate.getDate() + 5);
export class DeliverableStore extends ItemStore<Deliverable, IDeliverable, DeliverableStore.ContainerType> {

  filterProps: (keyof Deliverable | string)[] = [
    "to", "done", "delivered", "date", "tags", "milestones"
  ];
  defaultFilter: {[prop: string]: any[]} = {
    position:  [{ key: 'board'}],
    done:      [{key: false}],
    delivered: [{key: false}],
  };

  constructor(readonly rootStore: RootStore) {
    super(rootStore, Deliverable.create, DataType.Deliverable, [DataType.Question, DataType.Deliverable, DataType.Milestone]);

    makeObservable(this, {});
  }
  /*******************************************************************************************************************************************
   * DB Access
   ********************************************************************************************************************************************/
  protected setupStorage() {
    super.setupStorage();
    
    // Holds the latest user deliverables in the future
    let t = DeliverableStore.ContainerType.Priority;
    this.storage[t] = 
      new DataContainer<IDeliverable, Deliverable>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    true,
            pageSize:    this.overviewLimit,
            limited:     true,
            eventFilter: (d) => !d.archived && (isBefore(new Date(d.date), new Date()) || isToday(new Date(d.date))) && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(d.to)),
            conversion:  this.create,
            name: this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurUserID, () => this.storage[t].setQuery(this.getQueries[t]));

    // Hold next 5 user deliverables in the future
    t = DeliverableStore.ContainerType.Next;
    this.storage[t] = 
      new DataContainer<IDeliverable, Deliverable>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    true,
            pageSize:    this.overviewLimit,
            limited:     true,
            eventFilter: (d) => !d.archived && isAfter(new Date(d.date), new Date()) && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(d.to)),
            conversion:  this.create,
            name: this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurUserID, () => this.storage[t].setQuery(this.getQueries[t]));

    // Holds deliverables assigned to the current user
    t = DeliverableStore.ContainerType.To;
    this.storage[t] = 
      new DataContainer<Deliverable>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    true,
            pageSize:    this.overviewLimit,
            limited:     false,
            eventFilter: (d: Deliverable) => !d.archived && !d.done && !d.delivered && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(d.to)),
            conversion:  Deliverable.create,
            name:        this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurUserID, () => this.storage[t].setQuery(this.getQueries[t]));

    // Holds deliverables of the board of the current room
    t = DeliverableStore.ContainerType.Board;
    this.storage[t] = 
      new DataContainer<Deliverable>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    false,
            limited:     false,
            eventFilter: (d: Deliverable) => !d.archived && d.date && d.to && !d.done && Number(d.room_id) === this.rootStore.authStore.getCurRoomID,
            conversion:  Deliverable.create,
            name:        this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurRoomID, () => {
      this.rootStore.authStore.getCurRoomID && this.storage[t].setQuery(this.getQueries[t])
    });
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();
    // Register event callbacks
    this.eventCallBacks.before.patched.push(this.rootStore.meetingStore.countAffectedItems);
    this.eventCallBacks.after.all.push(this.roomFilter);
  }
  loadRoom = () => {
    super.loadRoom();
    this.storage.board.load();
  }

  loadDashboard = () => {
    super.loadDashboard();
    this.query = {
      to: {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
      date: {
        $gte: addWeeks(startOfMonth(this.rootStore.spaceStore.curDate), -2).toISOString(),
        $lte: addWeeks(endOfMonth(this.rootStore.spaceStore.curDate),    2).toISOString(),
      },
    };
    this.storage.next.load();
    this.storage.priority.load();
    this.storage.to.load();
    this.storage.board.clear();
  }

  clearDashboard = () => {
    this.storage.priority.clear();
    this.storage.next.clear();
    this.storage.to.clear();
  }

  get getQueries(): {[container: string]: Query} {
    return {
      [DeliverableStore.ContainerType.Next]: {
        date:    {$gte: new Date().toISOString()},
        to:      {$in: [this.rootStore.authStore.getCurUserID, ...(this.rootStore.authStore.getCurUser?.getUserGroups || [])]},
        $sort:   {"date": 1},
        $limit:  this.overviewLimit,
      }, 
      [DeliverableStore.ContainerType.To]: {
        to:      {$in: [this.rootStore.authStore.getCurUserID, ...(this.rootStore.authStore.getCurUser?.getUserGroups || [])]},
        delivered: false,
        done:      false,
        $sort:     {"date": 1},
        $limit:    this.overviewLimit,
      }, 
      [DeliverableStore.ContainerType.Board]: {
        to:        {$ne: null},
        done:      false,
        date:      {$ne: null},
        "deliverable.room_id":   this.rootStore.authStore.getCurRoomID,
      }, 
      [DeliverableStore.ContainerType.Priority]: {
        to:      {$in: [this.rootStore.authStore.getCurUserID, ...(this.rootStore.authStore.getCurUser?.getUserGroups || [])]},
        done:     false,
        date:     {$lte: new Date().toISOString() },
        $sort:     {"date": 1},
        $limit:    this.overviewLimit,
      }, 
    }
  }

  setActive(d?: IDeliverable): void {
    if(this.rootStore.meetingStore.getActiveMeeting && d) {
      const newD = this.create(d);
      if(newD.isRelevantForMeeting) newD.set("processed", true, !newD.isNew);
      this.activeStack.push(newD);
    } else {
      if(d) this.activeStack.push(this.create(d));
      else this.activeStack.pop();
    }
  }

  roomFilter = (data: IDeliverable) => { 
    if(!this.rootStore.authStore.getCurRoomID) return;

    if(Number(data.room_id) !== Number(this.rootStore.authStore.getCurRoomID)) {
      runInAction(() => {
        delete this.container[data.id];
        this.container = Object.assign({}, this.container);
      })
    }
  }

  
  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
}

export namespace DeliverableStore {
  export enum ContainerType {
    Next     = "next",    // Next X of that item type for the dashboard. Does not apply to questions
    To       = "to",      // Deliverables assigned to the current user
    Board    = "board",   // Deliverables shown on the board of the current room
    Priority = "priority",   // Deliverables shown on the board of the current room
  }
}